// Helpers do motor de renderização de campos personalizados (organization fields).
//
// Centraliza as regras que hoje estão duplicadas nas ~9 telas com `returnFields()`:
// normalização do valor por tipo, obrigatoriedade efetiva (override por status) e
// montagem do payload de save. Mantido puro (sem React) pra ser testável e reusável.

// Espelha o enum OrganizationFieldType do backend (FieldService.Business.Models).
export const FIELD_TYPE = {
  TEXT: 1,
  RADIO: 2,
  CHECKBOX: 3,
  SELECT: 4,
  NUMBER: 5,
  SELECT_MULTIPLE: 6,
  TEXT_MULTIPLE_LINE: 7,
  DECIMAL: 8,
  DATE: 9,
  REGEX_TEXT: 10,
  LIKERT: 11,
};

// Tipos cujo valor é escolhido a partir de uma lista de opções.
export const isOptionType = (type) =>
  type === FIELD_TYPE.RADIO || type === FIELD_TYPE.SELECT || type === FIELD_TYPE.SELECT_MULTIPLE;

/**
 * Obrigatoriedade EFETIVA do campo: parte de `isrequired` e aplica o override por status
 * (tb_organization_field_status). Mesma regra do backend, replicada pra UX (o backend valida de novo).
 */
export const isFieldRequired = (field, calledStatusId) => {
  if (!field || !field.isrequired) return false;
  const statuses = field.organizationfieldstatuses || [];
  const override = statuses.find((s) => s.statusid === calledStatusId);
  if (override && override.required === false) return false;
  return true;
};

/** O campo aceita edição? Respeita `allow_editing` da definição, além da flag global da organização. */
export const isFieldEditable = (field) => (field && field.allowediting) === true;

const pad2 = (n) => String(n).padStart(2, '0');

/** Date -> 'yyyy-MM-dd' (formato aceito pelo backend, sem fuso). */
export const toIsoDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** Converte o valor persistido (string) em Date, aceitando 'dd/MM/yyyy' e 'yyyy-MM-dd'. */
export const parseDateValue = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === 'string' && raw.includes('/')) {
    const [day, month, year] = raw.split('/');
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(`${raw}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Estado inicial de UM campo a partir do registro já salvo (pode não existir: campo sem valor).
 * Retorna sempre a mesma forma, independente do tipo — o motor lê daqui.
 *   { value, optionId, multipleIds }
 */
export const buildInitialFieldValue = (field, savedRecords) => {
  const type = field.organizationfieldtype;
  const records = (savedRecords || []).filter(
    (r) => Number(r.organizationfieldid) === Number(field.id)
  );

  if (type === FIELD_TYPE.SELECT_MULTIPLE) {
    const multipleIds = records
      .map((r) => r.organizationfieldoptionsvaluesid)
      .filter((id) => id !== null && id !== undefined)
      .map(Number);
    return { value: records.map((r) => r.value).filter(Boolean).join(','), optionId: null, multipleIds };
  }

  if (type === FIELD_TYPE.LIKERT) {
    const likertAnswers = {};
    records
      .filter((r) => r.organizationfieldlikertstatementid != null)
      .forEach((r) => {
        const stId = Number(r.organizationfieldlikertstatementid);
        const optId = Number(r.organizationfieldoptionsvaluesid) || null;
        if (!likertAnswers[stId]) likertAnswers[stId] = [];
        if (optId && !likertAnswers[stId].includes(optId)) likertAnswers[stId].push(optId);
      });
    const aggregate = records.find((r) => r.organizationfieldlikertstatementid == null);
    const aggregateValue = aggregate ? String(aggregate.value || '') : '';
    if (Object.keys(likertAnswers).length === 0 && aggregateValue) {
      Object.assign(likertAnswers, reconstructLikertAnswersFromAggregate(field, aggregateValue));
    }
    return {
      value: aggregateValue,
      optionId: null,
      multipleIds: [],
      likertAnswers,
    };
  }

  const record = records[0];
  if (!record) return { value: '', optionId: null, multipleIds: [] };

  const rawValue = record.value === null || record.value === undefined || record.value === 'null'
    ? ''
    : String(record.value);

  return {
    value: rawValue,
    optionId: record.organizationfieldoptionsvaluesid ?? null,
    multipleIds: [],
  };
};

/** O campo está vazio? (usado pra bloquear o save de campo obrigatório em branco) */
export const isFieldValueEmpty = (field, fieldValue) => {
  const type = field.organizationfieldtype;
  if (type === FIELD_TYPE.SELECT_MULTIPLE) {
    return !fieldValue || !fieldValue.multipleIds || fieldValue.multipleIds.length === 0;
  }
  // Checkbox nunca é "vazio": 'false' é uma resposta válida.
  if (type === FIELD_TYPE.CHECKBOX) return false;
  if (type === FIELD_TYPE.LIKERT) {
    const answers = (fieldValue && fieldValue.likertAnswers) || {};
    const statements = field.likertstatements || [];
    if (statements.length === 0) return true;
    return statements.some((st) => {
      const a = answers[Number(st.id)];
      return !Array.isArray(a) || a.length === 0;
    });
  }
  return !fieldValue || !String(fieldValue.value || '').trim();
};

const LIKERT_POSITIVE_KEYWORDS = [
  'satisfeit', 'adequad', 'ótim', 'otim', 'excelent', 'concord', 'rápid', 'rapid',
  'eficient', 'fácil', 'facil', 'bom', 'boa',
];
const LIKERT_NEGATIVE_KEYWORDS = [
  'insatisfeit', 'inadequad', 'péssim', 'pessim', 'ruim', 'discord', 'lent', 'demorad',
  'difícil', 'dificil', 'ineficient', 'não', 'nao',
];

/** Quebra o valor agregado de um Likert ("Inst: Resp; Inst2: Resp2") em pares { statement, answer }. */
export const parseLikertAggregateValue = (value) => {
  if (!value || typeof value !== 'string') return [];
  return value
    .split(';')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => {
      const separatorIndex = segment.indexOf(':');
      if (separatorIndex === -1) return { statement: '', answer: segment };
      return {
        statement: segment.slice(0, separatorIndex).trim(),
        answer: segment.slice(separatorIndex + 1).trim(),
      };
    });
};

/** Heurística por palavra-chave (pt-BR) pra colorir a resposta: não há peso numérico configurado na escala. */
export const guessLikertSentiment = (text) => {
  const normalized = String(text || '').toLowerCase();
  if (!normalized) return 'neutral';
  if (LIKERT_NEGATIVE_KEYWORDS.some((word) => normalized.includes(word))) return 'negative';
  if (LIKERT_POSITIVE_KEYWORDS.some((word) => normalized.includes(word))) return 'positive';
  return 'neutral';
};

/**
 * Reconstrói `likertAnswers` a partir da string agregada, casando nome de instrução/opção
 * com a definição do campo. Fallback para fluxos que só gravam a linha agregada (sem as
 * linhas de detalhe por instrução) — ex.: Formulário Externo (Mexx.Mexx2DO.Calleds).
 */
export const reconstructLikertAnswersFromAggregate = (field, aggregateValue) => {
  const statements = field.likertstatements || [];
  const answers = {};
  parseLikertAggregateValue(aggregateValue).forEach((segment) => {
    const statement = statements.find((st) => st.name === segment.statement);
    if (!statement) return;
    const ids = segment.answer
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean)
      .map((name) => {
        const option = (statement.options || []).find((opt) => opt.name === name);
        return option ? Number(option.id) : null;
      })
      .filter((id) => id !== null);
    if (ids.length > 0) answers[Number(statement.id)] = ids;
  });
  return answers;
};

/** Monta o payload do endpoint PUT /calledorganizationfields/quick-treat. */
export const buildSavePayload = (calledId, field, fieldValue) => {
  const base = {
    calledId: Number(calledId),
    organizationFieldId: Number(field.id),
  };

  if (field.organizationfieldtype === FIELD_TYPE.LIKERT) {
    const answers = (fieldValue && fieldValue.likertAnswers) || {};
    const statements = field.likertstatements || [];
    const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
    const likertRows = [];
    statements.forEach((st) => {
      asArray(answers[Number(st.id)]).forEach((optId) => {
        likertRows.push({ statementId: Number(st.id), optionId: Number(optId) || null });
      });
    });
    const value = statements
      .map((st) => {
        const ids = asArray(answers[Number(st.id)]);
        if (ids.length === 0) return null;
        const names = ids
          .map((id) => {
            const point = (st.options || []).find((o) => Number(o.id) === Number(id));
            return point ? point.name : '';
          })
          .filter(Boolean);
        return `${st.name}: ${names.join(', ')}`;
      })
      .filter(Boolean)
      .join('; ');
    return {
      ...base,
      value,
      organizationFieldOptionsValuesId: null,
      multipleOptionIds: [],
      likertRows,
    };
  }

  return {
    ...base,
    value: fieldValue.value === null || fieldValue.value === undefined ? '' : String(fieldValue.value),
    organizationFieldOptionsValuesId:
      fieldValue.optionId !== null && fieldValue.optionId !== undefined ? Number(fieldValue.optionId) : null,
    multipleOptionIds:
      field.organizationfieldtype === FIELD_TYPE.SELECT_MULTIPLE ? (fieldValue.multipleIds || []) : [],
    likertRows: null,
  };
};
